const SESSION_KEY = 'demo_tenant_slug';

/**
 * Resolves the active tenant slug using the following priority:
 *
 * 1. VITE_TENANT_SLUG — exclusive deployment mode (one tenant, locked at build time)
 * 2. /t/{slug} path — shareable entry URL; slug saved to sessionStorage, path stripped to /
 * 3. ?tenant= query param — shared deployment, user-facing URL
 * 4. sessionStorage — survives MSAL redirect roundtrips (set when 2 or 3 resolve)
 * 5. VITE_DEFAULT_TENANT — optional fallback for shared deployments (e.g. local dev)
 *
 * Returns null when no slug can be determined (show "no tenant" gate).
 */
export function resolveTenantSlug(): string | null {
  const fixed = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (fixed) return fixed;

  // /t/{slug}[/...] — entry URL pattern; strip the prefix so the tenant never
  // appears in subsequent navigation. Called once on bootstrap, before the router mounts.
  const pathMatch = /^\/t\/([^/]+)/.exec(window.location.pathname);
  if (pathMatch) {
    const slug = pathMatch[1];
    sessionStorage.setItem(SESSION_KEY, slug);
    window.history.replaceState(null, '', '/');
    return slug;
  }

  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('tenant');
  if (fromQuery) {
    sessionStorage.setItem(SESSION_KEY, fromQuery);
    return fromQuery;
  }

  const fromSession = sessionStorage.getItem(SESSION_KEY);
  if (fromSession) return fromSession;

  const defaultTenant = import.meta.env.VITE_DEFAULT_TENANT as string | undefined;
  return defaultTenant ?? null;
}

export function clearTenantSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}
